import { WORKSPACE_FEATURES } from '@itsappy/shared';

export type ProductSurface = 'lab' | 'chat' | 'meet';

export function getProductSurface(): ProductSurface {
  if (typeof window === 'undefined') return 'lab';
  if (window.location.hostname.startsWith('chat.')) return 'chat';
  if (window.location.hostname.startsWith('meet.')) return 'meet';
  return 'lab';
}

export function productHome(product = getProductSurface()): string {
  return product === 'chat' ? '/chat' : product === 'meet' ? '/meetings' : '/';
}

export function effectiveProductSurface(
  product: ProductSurface,
  features: readonly string[],
): ProductSurface {
  if (product === 'chat' && features.includes('chat')) return 'chat';
  if (product === 'meet' && features.includes('meetings')) return 'meet';
  if (features.length === 1 && features[0] === 'chat') return 'chat';
  if (features.length === 1 && features[0] === 'meetings') return 'meet';
  return 'lab';
}

/**
 * The features that say how much of the *product* an account holds.
 *
 * Itsappy AI is a helper layered over these rather than one of them, so it is
 * not evidence either way about how much of the workspace an account holds.
 * Company is left out for the same reason from the other direction: it is the
 * company's own surface — announcements, leave, the handbook — not one of the
 * products an account is scoped to. Counting it would mean that switching the
 * Company page off for one person also took Files, the inbox and People away,
 * which is not what an administrator asked for.
 */
/* Email hosting joins them, and for a third reason again: it is sold rather
   than granted, so most accounts in a workspace that buys it will never hold
   it. Counting it would mean the one person who administers the mailboxes has
   a "full" account and everybody else has a focused one — which would take
   Files, the inbox and People away from the whole company. */
const LAYERED_FEATURES: readonly string[] = ['assistant', 'company', 'mail'];
const SURFACE_FEATURES = WORKSPACE_FEATURES.filter(
  (feature) => !LAYERED_FEATURES.includes(feature),
);

/**
 * Whether this account is a full workspace account rather than a focused one.
 *
 * The measure is the workspace, not the number four: an owner who switches
 * Games off for everybody has not turned the whole company into chat-only
 * accounts, and the surfaces that belong to a full account — projects, files,
 * the inbox — must stay where they are. Counting Itsappy AI here broke the
 * same promise from the other side: switching it off for one account read as
 * "focused" and took search, the inbox and the banner away with it.
 */
export function hasFullAccess(
  features: readonly string[],
  disabledFeatures: readonly string[] = [],
): boolean {
  const available = SURFACE_FEATURES.filter(
    (feature) => !disabledFeatures.includes(feature),
  ).length;
  const held = features.filter((feature) => !LAYERED_FEATURES.includes(feature)).length;
  return available > 0 && held >= available;
}

export function featureHome(features: readonly string[]): string {
  if (features.includes('home')) return '/';
  if (features.includes('chat')) return '/chat';
  if (features.includes('spaces')) return '/spaces';
  if (features.includes('tasks')) return '/my-tasks';
  if (features.includes('meetings')) return '/meetings';
  if (features.includes('games')) return '/games';
  if (features.includes('company')) return '/company';
  return '/settings';
}
